import { Skeleton, TableCell, TableRow } from '@mui/material'
import PropTypes from 'prop-types'

/**
 * TbxQuotesDashboardSkeleton - Loading skeleton for QuotesDashboardTable
 *
 * Displays 5 skeleton rows with pulse animation matching the table structure.
 * Includes all possible columns (12-14) based on conditional feature flags.
 *
 * @param {Object} props
 * @param {boolean} [props.showIntegrationStatusColumn=false] - Show integration status column
 * @param {boolean} [props.showPaymentStatusColumn=false] - Show payment status column
 * @returns {JSX.Element}
 */
const TbxQuotesDashboardSkeleton = ({ showIntegrationStatusColumn = false, showPaymentStatusColumn = false }) => {
    // Generate 5 fixed rows
    const rows = Array.from({ length: 5 }, (_, index) => index)

    return (
        <>
            {rows.map((rowIndex) => (
                <TableRow key={`skeleton-row-${rowIndex}`}>
                    {/* Quote ID - 140px column */}
                    <TableCell
                        align="left"
                        sx={{ paddingBlock: '26px', paddingInline: '16px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width={100}
                        />
                    </TableCell>

                    {/* Work Order # - 120px column */}
                    <TableCell
                        align="left"
                        sx={{ paddingBlock: '26px', paddingInline: '16px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width={85}
                        />
                    </TableCell>

                    {/* Invoice # - 120px column */}
                    <TableCell
                        align="left"
                        sx={{ paddingBlock: '26px', paddingInline: '16px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width={85}
                        />
                    </TableCell>

                    {/* Customer - 160px column */}
                    <TableCell
                        align="left"
                        sx={{ paddingBlock: '26px', paddingInline: '16px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width={120}
                        />
                    </TableCell>

                    {/* Parts - 80px column, right-aligned */}
                    <TableCell
                        align="right"
                        sx={{ paddingBlock: '26px', paddingInline: '16px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px', marginLeft: 'auto' }}
                            variant="rectangular"
                            width={40}
                        />
                    </TableCell>

                    {/* Total Price - 120px column, right-aligned */}
                    <TableCell
                        align="right"
                        sx={{ paddingBlock: '26px', paddingInline: '16px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px', marginLeft: 'auto' }}
                            variant="rectangular"
                            width={80}
                        />
                    </TableCell>

                    {/* Last Modified By - 160px column */}
                    <TableCell
                        align="left"
                        sx={{ paddingBlock: '26px', paddingInline: '16px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width={120}
                        />
                    </TableCell>

                    {/* Last Modified - 120px column, right-aligned */}
                    <TableCell
                        align="right"
                        sx={{ paddingBlock: '26px', paddingInline: '16px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px', marginLeft: 'auto' }}
                            variant="rectangular"
                            width={85}
                        />
                    </TableCell>

                    {/* Source - 100px column */}
                    <TableCell
                        align="left"
                        sx={{ paddingBlock: '26px', paddingInline: '16px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width={70}
                        />
                    </TableCell>

                    {/* Status - 100px column */}
                    <TableCell
                        align="left"
                        sx={{ paddingBlock: '26px', paddingInline: '16px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px' }}
                            variant="rectangular"
                            width={70}
                        />
                    </TableCell>

                    {/* Integration Status - 100px column (conditional) */}
                    {showIntegrationStatusColumn ? (
                        <TableCell
                            align="left"
                            sx={{ paddingBlock: '26px', paddingInline: '16px' }}
                        >
                            <Skeleton
                                height={20}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={70}
                            />
                        </TableCell>
                    ) : null}

                    {/* Payment Status - 100px column (conditional) */}
                    {showPaymentStatusColumn ? (
                        <TableCell
                            align="left"
                            sx={{ paddingBlock: '26px', paddingInline: '16px' }}
                        >
                            <Skeleton
                                height={20}
                                sx={{ borderRadius: '4px' }}
                                variant="rectangular"
                                width={70}
                            />
                        </TableCell>
                    ) : null}

                    {/* Actions - 120px column, right-aligned */}
                    <TableCell
                        align="right"
                        sx={{ paddingBlock: '26px', paddingInline: '16px' }}
                    >
                        <Skeleton
                            height={20}
                            sx={{ borderRadius: '4px', marginLeft: 'auto' }}
                            variant="rectangular"
                            width={90}
                        />
                    </TableCell>
                </TableRow>
            ))}
        </>
    )
}

TbxQuotesDashboardSkeleton.propTypes = {
    showIntegrationStatusColumn: PropTypes.bool,
    showPaymentStatusColumn: PropTypes.bool,
}

export default TbxQuotesDashboardSkeleton
